import { useCallback, useMemo } from 'react';
import {
  last7days,
  momentDateRange2FetchParams,
  platformAll2Undefined,
  storeType2FetchParams,
  today,
} from '../ImplementData/utils';
import FilterProvider from '../ImplementData/components/Filter/Provider';
import { useDashboardFilter } from '../ImplementData/components/Filter/useFilter';
import { useRequest } from 'ahooks';
import {
  exportAppleContactDetails,
  exportAppleOrderDetails,
  exportAppleSpecialistStaffing,
  getAppleContactDetails,
  getAppleOrderDetails,
  getAppleSpecialistStaffing,
} from 'services/api/dashboard';
import { Button, message, Table, TableColumnsType, Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'components/DatePicker';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DownloadOutlined } from '@ant-design/icons';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import styled from '@emotion/styled';

const initialValue = {
  storeInfo: {},
  dateRange: last7days(),
};

type Type = 'ORDER' | 'CONVERSATION' | 'STAFF';

const textMap: Record<string, string> = {
  Store_Name: '店铺名称',
  Session_ID: '会话ID',
  Session_Start_Time: '会话开始时间',
  Session_End_Time: '会话结束时间',
  Customer_UID: '客户UID',
  'Contact_Queue (Based on Routing Group)': '联系队列（基于路由组）',
  Contact_Entry_Point: '联系入口点',
  Contact_Customer_Intent: '客户联系意图',
  Contact_End_Type: '联系结束类型',
  Bot_Handle_TS: '机器人处理时间戳',
  Survey_Sent_TS_Bot: '机器人发送问卷时间戳',
  Survey_Received_TS_Bot: '机器人接收问卷时间戳',
  Survey_Result_Bot: '机器人问卷结果',
  Survey_Tag_Bot: '机器人问卷标签',
  First_Responsive_Time_Bot_Sec: '机器人首次响应时间（秒）',
  Average_Response_Time_Bot_Sec: '机器人平均响应时间（秒）',
  Total_Response_Time_Bot_Sec: '机器人总响应时间（秒）',
  Total_Response_Pairs_Bot: '机器人总响应对数',
  Total_Customer_Lines_Bot: '客户总行数（机器人）',
  Total_Bot_Lines: '机器人总行数',
  Contact_Handling_Time_Bot_Sec: '机器人联系处理时间（秒）',
  Responsive_Indicator_Bot: '机器人响应指标',
  Bot_Handoff_TS: '机器人转人工时间戳',
  Bot_Handoff_Type: '机器人转人工类型',
  Queue_TS: '排队时间戳',
  Queue_Time_Sec: '排队时长（秒）',
  Specialist_Handle_TS: '专员处理时间戳',
  Specialist_First_Response_TS: '专员首次响应时间戳',
  Specialist_Group: '专员组',
  Specialist_Account: '专员账号',
  Specialist_ID: '专员ID',
  First_Responsive_Time_Specialist_Sec: '专员首次响应时间（秒）',
  Average_Response_Time_Specialist_Sec: '专员平均响应时间（秒）',
  Total_Response_Time_Specialist_Sec: '专员总响应时间（秒）',
  Total_Response_Pairs_Specialist: '专员总响应对数',
  Total_Customer_Lines_Specialist: '客户总行数（专员）',
  Total_Specialist_Lines: '专员总行数',
  Contact_Handling_Time_Specialist_Sec: '专员联系处理时间（秒）',
  Responsive_Indicator_Specialist: '专员响应指标',
  Transfer_Record: '转接记录',
  Survey_Sent_TS_Specialist: '专员发送问卷时间戳',
  Survey_Received_TS_Specialist: '专员接收问卷时间戳',
  Survey_Result_Specialist: '专员问卷结果',
  Survey_Tag_Specialist: '专员问卷标签',
  PreSales_Indicator: '售前指标',
  Order_Placed_Ind: '下单指标',
  Order_Revenue: '订单收入',
  'Order Currency': '订单币种',
  Refund_Ind: '退款指标',
  Refund_Revenue: '退款收入',
  'Paid_Order_Details (Assisted Order ID)': '已支付订单详情（助攻订单ID）',
  Order_Attribution: '订单归因',
  'Contact Driver': '联系驱动因素',
  Order_ID: '订单ID',
  Payment_Method: '支付方式',
  Product_Name: '商品名称',
  Product_ID: '商品ID',
  Product_Unit: '商品单位',
  Product_Amount: '商品金额',
  Order_Create_TS: '订单创建时间戳',
  Payment_Completed_TS: '支付完成时间戳',
  Promised_Shipment_TS: '承诺发货时间戳',
  Shipment_TS: '发货时间戳',
  Delivery_TS: '送达时间戳',
  Order_Cancelled_TS: '订单取消时间戳',
  Order_Returned_TS: '订单退货时间戳',
  Order_Refuned_TS: '订单退款时间戳',
  Order_Completed_TS: '订单完成时间戳',
  Service_Status: '服务状态',
  Cancel_Reason: '取消原因',
  Return_Reason: '退货原因',
  Order_Status: '订单状态',
  Assisted_Indicator: '助攻指标',
  Assisted_Attribution: '助攻归因',
  Assisted_Attribution_ID: '助攻归因ID',
  Refund_Amount: '退款金额',
  Date: '日期',
  Staffed_Hour: '排班小时',
  Online_Time: '在线时间',
  Engagement_Time: '互动时间',
  '… Other status Time': '其他状态时间',
  Average_Responsive_Time_Working_hour: '平均响应时间_工作时段',
  Average_Responsive_Time: '平均响应时间',
  First_Responsive_Time_Working_Hour: '首次响应时间_工作时段',
  First_Responsive_Time_Sec: '首次响应时间_秒',
  Response_Rate_3min_Working_Hour: '3分钟响应率_工作时段',
  Response_Rate_3min_line: '3分钟响应率_行',
  Alert_Sessions: '告警会话',
  PreSales_Customers_CNT: '售前客户数',
  Assisted_Order_Customers_CNT: '助攻订单客户数',
  'Paying _Customers_CNT': '支付客户数',
  Refund_Customers_CNT: '退款客户数',
  'Assisted_Revenue(CNY)': '助攻收入（人民币）',
  'Refund_Revenue(CNY)': '退款收入（人民币）',
  'Assisted_Revenue_Excl_Refund(CNY)': '助攻收入（不含退款，人民币）',
  Conversion_Rate: '转化率',
};

const timeRender = (time: string) => (time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-');
const durationRender = (duration: string, t: any) => {
  if (!duration) {
    return '-';
  }
  const d = dayjs.duration(Number(duration), 'seconds');
  const days = d.days();
  const hours = d.hours();
  const minutes = d.minutes();
  const seconds = d.seconds();
  if (days) {
    return t('{x}天{y}小时{z}分钟', {
      x: days,
      y: hours,
      z: minutes,
    });
  }
  if (hours) {
    return t('{y}小时{z}分钟', {
      y: hours,
      z: minutes,
    });
  }
  if (minutes) {
    return `${minutes} ${t('marketing.分钟')}`;
  }
  return `${seconds} ${t('translation.秒')}`;
};
const secondsRender = (seconds: string) => {
  return seconds ? parseInt(seconds) : '-';
};
const defaultRender = (text: string) => text || '-';

const fieldsRenderMap = {
  Order_Create_TS: timeRender,
  Payment_Completed_TS: timeRender,
  Shipment_TS: timeRender,
  Promised_Shipment_TS: timeRender,
  Delivery_TS: timeRender,
  Order_Returned_TS: timeRender,
  Order_Refuned_TS: timeRender,
  Session_Start_Time: timeRender,
  Session_End_Time: timeRender,
  Bot_Handle_TS: timeRender,
  Bot_Handoff_TS: timeRender,
  Queue_TS: timeRender,
  Specialist_Handle_TS: timeRender,
  Specialist_First_Response_TS: timeRender,
  Online_Time: durationRender,
  Engagement_Time: durationRender,
  First_Responsive_Time_Bot_Sec: secondsRender,
  Average_Response_Time_Bot_Sec: secondsRender,
  Total_Response_Time_Bot_Sec: secondsRender,
  Queue_Time_Sec: secondsRender,
  First_Responsive_Time_Specialist_Sec: secondsRender,
  Average_Response_Time_Specialist_Sec: secondsRender,
  Total_Response_Time_Specialist_Sec: secondsRender,
  Contact_Handling_Time_Specialist_Sec: secondsRender,
};

function Content({ type }: { type: Type }) {
  const { t } = useTranslate();

  const filterCtx = useDashboardFilter();

  const platform = useMemo(() => {
    return platformAll2Undefined(filterCtx.platform);
  }, [filterCtx.platform]);

  const fetchParams: Dashboard.BasicParams = useMemo(() => {
    const list = filterCtx.storeInfo.list || [];

    const { endTime } = momentDateRange2FetchParams(filterCtx.dateRange);

    return {
      startTime: endTime,
      endTime,
      platform,
      storeType: storeType2FetchParams(filterCtx.storeType),
      storeIds: list.map((x) => x.id),
      departmentIds: filterCtx.departments?.map((_) => _.id),
    };
  }, [
    platform,
    filterCtx.dateRange,
    filterCtx.storeType,
    filterCtx.storeInfo.list,
    filterCtx.departments,
  ]);

  const { data, loading, runAsync } = useRequest(
    (page: { page: number; pageSize: number } = { page: 1, pageSize: 10 }) => {
      const api = {
        ORDER: getAppleOrderDetails,
        CONVERSATION: getAppleContactDetails,
        STAFF: getAppleSpecialistStaffing,
      }[type];
      return api({
        ...fetchParams,
        ...page,
      }).then((res) => {
        const { page, pageSize, total, fields, rows } = res;
        return {
          page: {
            pageNo: page,
            pageSize,
            total,
          },
          list: rows.map((row) => {
            return fields.reduce<Record<string, string>>((res, field, index) => {
              res[field] = row[index];
              return res;
            }, {});
          }),
          fields,
        };
      });
    },
    {
      refreshDeps: [fetchParams],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const lang = useSystemLanguageValue();

  const columns = useMemo<TableColumnsType<any> | undefined>(() => {
    return data?.fields.map((field, index) => {
      return {
        key: field,
        dataIndex: field,
        title:
          lang === 'zh-CN'
            ? textMap[field] || field.split('_').join(' ')
            : field.split('_').join(' '),
        fixed: index === 0 || (type === 'CONVERSATION' && index === 1) ? 'left' : undefined,
        render: (text) =>
          (fieldsRenderMap[field as keyof typeof fieldsRenderMap] || defaultRender)(text, t),
      };
    });
  }, [data, lang, type, t]);

  return (
    <Container>
      <Table
        loading={loading}
        dataSource={data?.list}
        columns={columns}
        pagination={
          data?.page
            ? {
                current: data.page.pageNo,
                pageSize: data.page.pageSize,
                total: data.page.total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (x) => t('feedback.共x项', { x }),
                onChange: (page, pageSize) => runAsync({ page, pageSize }),
              }
            : undefined
        }
        scroll={{
          x: true,
        }}
      />
    </Container>
  );
}

function ExtraNode<
  T extends {
    dateRange: [Dayjs, Dayjs];
  },
>(props: { value: T; onChange: React.Dispatch<React.SetStateAction<T>>; max?: Dayjs }) {
  const { value, onChange, max } = props;

  const min = useMemo(() => dayjs().subtract(30, 'days').startOf('day'), []);

  const { t } = useTranslate();

  const disabledDate = useCallback(
    (date: Dayjs) => {
      return !date.isBetween(min, max, 'day', '[]');
    },
    [min, max],
  );

  return (
    <DatePicker
      allowClear={false}
      value={value.dateRange[1]}
      disabledDate={disabledDate}
      onChange={(value) => {
        if (!value) {
          return;
        }
        initialValue.dateRange = [value, value];
        onChange((pre) => ({
          ...pre,
          dateRange: [value, value],
        }));
      }}
      dateRender={(current) => {
        if (current.isBetween(max, today(), 'day', '(]')) {
          return (
            <Tooltip title={t('ViewDashboard.当前日期数据正在结算中，暂不支持查看。')}>
              <div className="ant-picker-cell-inner">{current.date()}</div>
            </Tooltip>
          );
        }
        return <div className="ant-picker-cell-inner">{current.date()}</div>;
      }}
    />
  );
}

function AppleDetail({ type }: { type: Type }) {
  const { t } = useTranslate();

  const { loading, run } = useExportFile();

  return (
    <FilterProvider
      initialValue={initialValue}
      rightExtra={ExtraNode}
      dashboardName="OrderOverview"
      scene="ORDER_STAT"
      showPlatformAll
      exportNodeRender={(filter) => {
        return (
          <Tooltip title={t('export_data')}>
            <Button
              loading={loading}
              icon={<DownloadOutlined />}
              onClick={() => {
                run(() => {
                  const {
                    dateRange,
                    platform,
                    storeType,
                    storeInfo: { list = [] },
                    departments,
                  } = filter;
                  const { endTime } = momentDateRange2FetchParams(dateRange);
                  const api = {
                    ORDER: exportAppleOrderDetails,
                    CONVERSATION: exportAppleContactDetails,
                    STAFF: exportAppleSpecialistStaffing,
                  }[type];
                  return api({
                    startTime: endTime,
                    endTime,
                    platform: platformAll2Undefined(platform),
                    storeType: storeType2FetchParams(storeType),
                    storeIds: list.map((x) => x.id),
                    departmentIds: departments?.map((_) => _.id),
                  });
                });
              }}
            />
          </Tooltip>
        );
      }}
    >
      <Content type={type} />
    </FilterProvider>
  );
}

const Container = styled.div`
  height: calc(100vh - 214px);
  padding: 16px;
  background: var(--fett-bg-panel);
  overflow: auto;
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    white-space: nowrap;
  }
`;

export default AppleDetail;
